import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import GrIconTile from "./GrIconTile";
import { gr } from "../tokens";

const PHONE = "@media (max-width: 599.95px)";

// Banner on top of a page: icon, title, one line, one action. Accent on the left edge.
// tone: brand (default), warning (amber; reminders the member should notice) or ok (green; done).
// inline: a light strip inside a card (no shadow, no edge accent, tinted, plain icon) instead of a page banner.
const TONES = {
  brand: { accent: gr.color.brand, bg: gr.color.surface, tile: "brand", soft: gr.color.tint, fg: gr.color.brandDark },
  warning: { accent: gr.color.late, bg: gr.color.lateSoft, tile: "neutral", soft: gr.color.lateSoft, fg: gr.color.late },
  ok: { accent: gr.color.ok, bg: gr.color.okSoft, tile: "ok", soft: gr.color.okSoft, fg: gr.color.ok },
};

export default function GrNotice({
  icon,
  title,
  text,
  action,
  tone = "brand",
  inline = false,
  role = "status",
  sx,
}) {
  const t = TONES[tone] || TONES.brand;
  const inlineSx = inline
    ? {
        gap: gr.space[3],
        padding: `${gr.space[3]} ${gr.space[4]}`,
        borderRadius: gr.radius.tile,
        borderLeft: "none",
        background: t.soft,
        boxShadow: "none",
        [PHONE]: { padding: `${gr.space[3]} ${gr.space[4]}` },
      }
    : {};
  return (
    <Box
      role={role}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: gr.space[4],
        padding: `${gr.space[4]} ${gr.space[6]}`,
        borderRadius: gr.radius.card,
        borderLeft: `4px solid ${t.accent}`,
        background: t.bg,
        boxShadow: gr.shadow.card,
        [PHONE]: { flexWrap: "wrap", padding: gr.space[4] },
        ...inlineSx,
        ...sx,
      }}
    >
      {icon && inline ? (
        <Box sx={{ flex: "none", display: "flex", color: t.fg }}>{icon}</Box>
      ) : icon ? (
        <GrIconTile size="sm" tone={t.tile}>
          {icon}
        </GrIconTile>
      ) : null}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: "2px",
        }}
      >
        <Box
          component="span"
          sx={{
            fontSize: gr.font.size.md,
            fontWeight: gr.font.weight.semibold,
            color: gr.color.ink,
          }}
        >
          {title}
        </Box>
        {text ? (
          <Box
            component="span"
            sx={{ fontSize: gr.font.size.sm, color: gr.color.ink2 }}
          >
            {text}
          </Box>
        ) : null}
      </Box>
      {action ? (
        <Box
          sx={{
            flex: "none",
            [PHONE]: { flexBasis: "100%", "& > *": { width: "100%" } },
          }}
        >
          {action}
        </Box>
      ) : null}
    </Box>
  );
}

GrNotice.propTypes = {
  icon: PropTypes.node,
  title: PropTypes.node.isRequired,
  text: PropTypes.node,
  action: PropTypes.node,
  tone: PropTypes.oneOf(["brand", "warning", "ok"]),
  inline: PropTypes.bool,
  role: PropTypes.string,
  sx: PropTypes.object,
};
